import { useTranslate } from '../../application/i18n/LocaleProvider';
import { isFacetActive } from '../../domain/cars/carFilters';
import FacetPill from './FacetPill';

/**
 * The one facet that stays on the screen. Everything else is behind the sheet.
 *
 * All four facets were drawn inline once, and on a 13 mini they filled the fold
 * — the catalogue opened on a wall of controls and no car, which is the wrong
 * trade for a product whose artwork is the argument. Moving all of them into
 * the sheet fixed that and overcorrected: the commonest thing a reader does
 * here is cut the shelf to one decade, and making that a three-tap round trip
 * through a modal is a lot of ceremony for two options.
 *
 * So: the primary axis is a row, the rest are a tap away. `PRIMARY_FACET` is
 * `decade` because it is the axis this catalogue is ABOUT — the product is
 * eighties and nineties cars, and which of the two is the question a browser
 * actually arrives with. It is also the shortest facet, at two or three
 * options, so it fits one row without scrolling; `class` and `origin` run to
 * five and would not.
 *
 * The sheet still carries the decade too. A control that appears in two places
 * is a duplication worth paying for here — the sheet is "all the filters", and
 * one missing from it because it happens to be on the screen behind would be
 * the kind of gap nobody can find on purpose.
 */
export const PRIMARY_FACET = 'decade';

export default function GarageQuickFilter({ facets, filters, onToggle }) {
    const t = useTranslate();
    const facet = facets.find((candidate) => candidate.key === PRIMARY_FACET);

    // `listFacets` drops a facet the catalogue cannot fill, and one with a
    // single option is a label rather than a choice — either way there is
    // nothing here to draw.
    if (!facet || facet.options.length < 2) {
        return null;
    }

    return (
        <div
            className="garage-quick-filter"
            role="group"
            aria-labelledby={`quick-facet-${facet.key}`}
        >
            <p className="visually-hidden" id={`quick-facet-${facet.key}`}>{t(facet.labelKey)}</p>
            {facet.options.map((option) => (
                <FacetPill
                    key={option.value}
                    label={t(option.labelKey)}
                    isActive={isFacetActive(filters, facet.key, option.value)}
                    onToggle={() => onToggle(facet.key, option.value)}
                />
            ))}
        </div>
    );
}
